import React, { useState } from 'react';
import { blockNonNumericKeys, getNumericFieldError } from '@/utils/numericValidation';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, Edit2, Eye, Loader2 } from 'lucide-react';
import { NumericField } from '../../components/NumericField';

// Common glass thicknesses for curtain wall
const COMMON_GLASS_THICKNESSES = [
  { value: 0.125, label: '1/8" (3mm)' },
  { value: 0.1875, label: '3/16" (5mm)' },
  { value: 0.25, label: '1/4" (6mm)' },
  { value: 0.3125, label: '5/16" (8mm)' },
  { value: 0.375, label: '3/8" (10mm)' },
  { value: 0.5, label: '1/2" (12mm)' },
  { value: 0.625, label: '5/8" (16mm)' },
  { value: 0.75, label: '3/4" (19mm)' },
  { value: 1.0, label: '1" (25mm)' },
];

// Glass type specifications
const glassTypeSpecs = [
  { id: 'tempered', name: 'Tempered' },
  { id: 'laminated', name: 'Laminated' },
  { id: 'insulated', name: 'Insulated' },
  { id: 'low-e', name: 'Low-E' },
  { id: 'tinted', name: 'Tinted' },
  { id: 'reflective', name: 'Reflective' },
  { id: 'spandrel', name: 'Spandrel' },
  { id: 'frosted', name: 'Frosted' },
];

export const CWGlassTypesSection = ({
  // Data
  glassTypes,
  selectedCWGlassTypeIds,
  expandedCWGlassTypeIds,
  newGlassType,
  editingGlassType,
  showGlassTypeDialog,
  operationLoading,
  // Permissions
  canEditCSIItems,
  canManageProject,
  // Refs
  cwGridSizeRef,
  // Handlers
  setNewGlassType,
  setShowGlassTypeDialog,
  setEditingGlassType,
  setExpandedCWGlassTypeIds,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreateGlassType,
  handleEditGlassType,
  handleDeleteGlassType,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const numericErrors = [
    getNumericFieldError(newGlassType.warrantyYears, { integer: true, gtZero: true, label: 'Warranty' }),
    newGlassType.hasGrid
      ? getNumericFieldError(newGlassType.gridSize_in, { required: true, min: 0, allowFraction: true, label: 'Grid size' })
      : null,
  ];
  const handleSubmit = (e) => {
    setSubmitAttempted(true);
    if (numericErrors.some(Boolean)) {
      e.preventDefault();
      return;
    }
    handleCreateGlassType(e);
  };

  return (
    <TabsContent value="cw-glass-types">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Glass Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && glassTypes.length > 0 && (
            <>
              <Button variant="outline" onClick={() => selectAllItems('cwGlassType')}>
                {selectedCWGlassTypeIds.length === glassTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={() => confirmBulkDelete('cwGlassType')}
                disabled={selectedCWGlassTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedCWGlassTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showGlassTypeDialog} onOpenChange={(open) => {
              setSubmitAttempted(false);
              setShowGlassTypeDialog(open);
              if (!open) {
                setEditingGlassType(null);
                setNewGlassType({
                  name: '',
                  manufacturer: '',
                  model: '',
                  thickness_in: null,
                  colorCode: '',
                  specifications: [],
                  warrantyYears: ''
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Glass Type
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingGlassType ? 'Edit Glass Type' : 'Add Glass Type'}</DialogTitle>
                <DialogDescription>Define glass specifications that can be referenced in window glass lites</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type (e.g., GL1, GL2) *</Label>
                    <Input value={newGlassType.name} onChange={(e) => setNewGlassType({...newGlassType, name: e.target.value})} placeholder="e.g., GL1, GL2, GL3" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Manufacturer *</Label>
                    <Input value={newGlassType.manufacturer} onChange={(e) => setNewGlassType({...newGlassType, manufacturer: e.target.value})} placeholder="e.g., Guardian, PPG, Cardinal" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Model *</Label>
                    <Input value={newGlassType.model} onChange={(e) => setNewGlassType({...newGlassType, model: e.target.value})} placeholder="e.g., SunGuard, Solarban 70XL" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Thickness (in) *</Label>
                    <Select 
                      value={newGlassType.thickness_in?.toString() || ''} 
                      onValueChange={(val) => setNewGlassType({...newGlassType, thickness_in: parseFloat(val)})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select thickness" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_GLASS_THICKNESSES.map((t) => (
                          <SelectItem key={t.value} value={t.value.toString()}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Color Code (HEX/RAL)</Label>
                    <Input value={newGlassType.colorCode} onChange={(e) => setNewGlassType({...newGlassType, colorCode: e.target.value})} placeholder="#FFFFFF or RAL 9016" />
                  </div>
                  <div className="space-y-2">
                    <Label>Build</Label>
                    <Input 
                      value={newGlassType.build || ''} 
                      onChange={(e) => setNewGlassType({...newGlassType, build: e.target.value})} 
                      placeholder="e.g., 1/4 + 1/2 Air + 1/4" 
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Specifications</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border rounded-md">
                    {glassTypeSpecs.map((spec) => (
                      <label
                        key={spec.id}
                        htmlFor={`cw-spec-${spec.id}`}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <Checkbox
                          id={`cw-spec-${spec.id}`}
                          checked={newGlassType.specifications.includes(spec.name)}
                          onCheckedChange={(checked) => {
                            const updated = checked
                              ? [...newGlassType.specifications, spec.name]
                              : newGlassType.specifications.filter(s => s !== spec.name);
                            setNewGlassType({...newGlassType, specifications: updated});
                          }}
                        />
                        <span className="text-sm">{spec.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Warranty Period */}
                <div className="space-y-2">
                  <Label>Warranty Period (Years)</Label>
                  <NumericField
                    placeholder="e.g., 10"
                    value={newGlassType.warrantyYears}
                    onChange={(v) => setNewGlassType({ ...newGlassType, warrantyYears: v })}
                    rules={{ integer: true, gtZero: true, label: 'Warranty' }}
                    showError={submitAttempted}
                    data-testid="cw-glass-warranty-input"
                    errorTestId="cw-glass-warranty-error"
                  />
                </div>
                
                {/* Grid Checkbox */}
                <div className="space-y-2 border-t pt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={newGlassType.hasGrid || false}
                      onCheckedChange={(isChecked) => {
                        setNewGlassType({
                          ...newGlassType, 
                          hasGrid: isChecked,
                          gridSize_in: isChecked ? newGlassType.gridSize_in : null
                        });
                        if (isChecked) {
                          setTimeout(() => cwGridSizeRef.current?.focus(), 100);
                        }
                      }}
                    />
                    <span>This glass type has grid</span>
                  </label>
                </div>
                
                {newGlassType.hasGrid && (
                  <div className="space-y-2">
                    <Label>Grid Size (in) *</Label>
                    <NumericField
                      value={newGlassType.gridSize_in}
                      onChange={(v) => setNewGlassType({ ...newGlassType, gridSize_in: v })}
                      rules={{ required: true, min: 0, allowFraction: true, label: 'Grid size' }}
                      showError={submitAttempted}
                      placeholder="e.g., 6"
                      required
                      autoFocus={newGlassType.hasGrid}
                      data-testid="cw-glass-grid-size-input"
                      errorTestId="cw-glass-grid-size-error"
                    />
                  </div>
                )}
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowGlassTypeDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={operationLoading}>
                    {operationLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {editingGlassType ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>{editingGlassType ? 'Update' : 'Create'} Glass Type</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {glassTypes.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
          <p>No glass types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canEditCSIItems && (
                  <th className="w-12 px-3 py-3 text-left">
                    <Checkbox
                      checked={selectedCWGlassTypeIds.length === glassTypes.length && glassTypes.length > 0}
                      onCheckedChange={() => selectAllItems('cwGlassType')}
                    />
                  </th>
                )}
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="w-36 px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="w-36 px-3 py-3 text-left font-semibold text-slate-700">Model</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Build</th>
                {canEditCSIItems && (
                  <th className="w-28 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {glassTypes.map((glass, idx) => (
                <React.Fragment key={glass.id}>
                  <tr 
                    className={`border-b hover:bg-slate-50 transition-colors ${
                      selectedCWGlassTypeIds.includes(glass.id) ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    {canEditCSIItems && (
                      <td className="w-12 px-3 py-2">
                        <Checkbox
                          checked={selectedCWGlassTypeIds.includes(glass.id)}
                          onCheckedChange={(checked) => toggleItemSelection(glass.id, 'cwGlassType', { target: { checked } })}
                        />
                      </td>
                    )}
                    <td className="w-24 px-3 py-2 font-medium text-slate-900">{glass.name}</td>
                    <td className="w-36 px-3 py-2 text-slate-600 truncate">{glass.manufacturer || '-'}</td>
                    <td className="w-36 px-3 py-2 text-slate-600 truncate">{glass.model || '-'}</td>
                    <td className="px-3 py-2 text-slate-600 truncate">{glass.build || '-'}</td>
                    {canEditCSIItems && (
                      <td className="w-28 px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={`h-7 w-7 p-0 transition-colors ${
                              expandedCWGlassTypeIds.includes(glass.id) 
                                ? 'bg-orange-500 text-white hover:bg-orange-600' 
                                : 'text-slate-500 hover:bg-orange-500 hover:text-white'
                            }`}
                            onClick={() => setExpandedCWGlassTypeIds(prev => 
                              prev.includes(glass.id) 
                                ? prev.filter(id => id !== glass.id) 
                                : [...prev, glass.id]
                            )}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditGlassType(glass)}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => handleDeleteGlassType(glass.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {/* Expanded Details Row */}
                  {expandedCWGlassTypeIds.includes(glass.id) && (
                    <tr className="bg-slate-50 border-b">
                      {canEditCSIItems && <td className="w-12"></td>}
                      <td colSpan={canEditCSIItems ? 5 : 4} className="px-3 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                          <div>
                            <span className="font-semibold text-slate-700">Thickness:</span>
                            <span className="ml-2 text-slate-600">{glass.thickness_in ? `${glass.thickness_in}"` : '-'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">Color Code:</span>
                            <span className="ml-2 text-slate-600">{glass.colorCode || '-'}</span>
                          </div>
                          {glass.hasGrid && (
                            <div>
                              <span className="font-semibold text-slate-700">Grid Size:</span>
                              <span className="ml-2 text-slate-600">{glass.gridSize_in ? `${glass.gridSize_in}"` : 'Yes'}</span>
                            </div>
                          )}
                          {glass.warrantyYears && (
                            <div>
                              <span className="font-semibold text-slate-700">Warranty:</span>
                              <span className="ml-2 text-slate-600">{glass.warrantyYears} Years</span>
                            </div>
                          )}
                          {glass.specifications && glass.specifications.length > 0 && (
                            <div className="col-span-2 md:col-span-4 mt-1">
                              <span className="font-semibold text-slate-700">Specifications:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {glass.specifications.map((spec, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-slate-200 rounded text-xs">{spec}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TabsContent>
  );
};

export default CWGlassTypesSection;
